import { ApplicationBuildId, UUID } from "../hooks/types";
import { OrderedSessionState } from "./session-state";

export enum DeviceType {
  Desktop = "pc-desktop-client",
  Standalone = "vr-standalone",
  Browser = "browser",
  Remote = "pc-remote-client",
  Unknown = "unknown",
}

type DeviceVendorName =
  | "htc"
  | "oculus"
  | "pico"
  | "meta"
  | "apple"
  | "magic-leap"
  | "magicleap";

interface ClientDeviceBase {
  identifier: string;
  isOnline: boolean;
  name: string;
  type: DeviceType;
  version?: string;
  targetVersion?: string;
  isOutdated?: boolean;
  modelName?: string;
  vendorName?: DeviceVendorName;
}

export interface ClientDeviceData extends ClientDeviceBase {
  lastSeenDateTime: string;
}

export enum SessionState {
  RequestingRenderMachine = "RequestingRenderMachine",
  RenderMachineReady = "RenderMachineReady",
  RequestedLogin = "RequestedLogin",
  RequestedAppInstallation = "RequestedAppInstallation",
  AppInstalled = "AppInstalled",
  RequestedLaunchedApp = "RequestedLaunchedApp",
  LaunchedApp = "LaunchedApp",
  RequestedTermination = "RequestedTermination",
  Terminated = "Terminated",
}

export interface SessionData {
  id?: string;
  state: OrderedSessionState;
  appLaunched?: boolean;
  progress?: number;
  message?: string;
  expectedWaitTimeSec?: number;
  deviceIdentifier?: string;
  deviceVendorName?: string | null;
  deviceModelName?: string | null;
  applicationBuildId?: ApplicationBuildId;
  type: SessionType;
  launchArguments?: string;
  extraLaunchArguments?: string;
  ipAddress?: string;
  renderRegion?: string;
  renderCloudProvider?: string;
  organizationId?: string;
  vmSize?: string;
  vmImage?: string;
  xrEncryption?: boolean;
  debugModeEnabled: boolean;
  issues: {
    hasBadLatency: boolean;
    hasPackageLoss: boolean;
    hasHighJitter: boolean;
    hasNotEnoughBandwidth: boolean;
  };
  error?: ErrorMessage;
  streamingGatewayEndpoint?: string | null;
  httpsEndpoints?: HttpsEndpoints | null;
  wireguardSettings?: WireguardSettings;
}

export interface NotificationMessage {
  alert: boolean;
  notificationType:
    | "MaxSoftSessionRunTime"
    | "MaxSessionRunTime"
    | "HighLatency"
    | "SessionInactivityTimeoutTime"
    | "VirtualMachineAssignedToSession"
    | "VirtualMachineDestroyedBecauseOfError";
  level: "Success" | "Warning" | "Error" | "Information";
  params?: { [key: string]: string };
  id?: string;
}

export enum SessionType {
  CloudRenderedNonVR = "browser",
  CloudRenderedVR = "cloud",
  LocallyRenderedWindows = "desktop",
  LocallyRenderedStandalone = "standalone",
  Unknown = "unknown",
}

export type InstalledApp = { id: number; appId: string };

export interface ClientDevice extends ClientDeviceBase {
  lastSeenDateTime: number;
  installedApps: InstalledApp[];
  wireguardPublicKey?: string | null;
}

export interface DefaultSessionOverviewMessage {
  id: string;
  state: SessionState;
  appId: string;
  launchArguments?: string;
  extraLaunchArguments?: string;
  appTargetPlatform: string;
  deviceIdentifier: string;
  deviceTargetPlatform: DeviceType;
  appLaunched: boolean;
}

export interface CloudRenderedSessionOverviewMessage extends DefaultSessionOverviewMessage {
  ipAddress?: string;
  xrEncryption?: boolean;
  renderRegion?: string;
  renderCloudProvider?: string;
  vmSize?: string;
  debugModeEnabled: boolean;
  vmImage?: string;
  streamingGatewayEndpoint?: string | null;
  httpsEndpoints?: HttpsEndpoints | null;
  wireguardSettings?: WireguardSettings;
}

export interface InstalledAppsMessage {
  apps: InstalledApp[];
  deviceIdentifier: string;
}

export interface ClientIdentificationMessage {
  identifier: string;
  name: string;
  type: DeviceType;
  version: string;
  /** Optional vendor name (e.g. "meta", "pico") used for matching launch configs by xr_platform. */
  vendorName?: string;
  /** Optional device model name (e.g. "quest 3"). */
  modelName?: string;
  /**
   * XR streaming protocol version this client supports (e.g. "CXR6.1.0").
   * When set on a VrStandalone device, Session Management provisions CloudXR
   * HTTPS gateway endpoints for sessions created by this device.
   */
  xrStreamingProtocol?: string;
}

export interface ClientIdentificationListMessage {
  clients: ClientDeviceData[];
}

export interface SessionCreatedMessage {
  id: string;
  deviceIdentifier: string;
  appId: string;
  launchArguments?: string;
  extraLaunchArguments?: string;
  appTargetPlatform: string;
  deviceTargetPlatform: DeviceType;
}

interface WireguardSettings {
  clientCidr: string;
  serverCidr: string;
  listenPort: number;
  publicPort?: number;
  host?: string;
}

export interface CloudRenderedSessionCreatedMessage extends SessionCreatedMessage {
  renderRegion: string;
  renderCloudProvider: string;
  vmImage?: string;
  xrEncryption: boolean;
  wireguardSettings?: WireguardSettings;
}

interface DefaultSessionStartupProgressMessage {
  sessionId: string;
  progress: number;
  message: string;
  sessionState: string;
  appId: string;
  appBuildId: string;
  launchArguments: string | null;
  extraLaunchArguments: string | null;
  renderRegion: string;
}

interface CloudRenderedSessionStartupProgressMessage extends DefaultSessionStartupProgressMessage {
  renderCloudProvider: string;
  expectedWaitTimeSec: number;
  vmSize: string;
  xrEncryption: boolean;
}

export type SessionStartupProgressMessage =
  | DefaultSessionStartupProgressMessage
  | CloudRenderedSessionStartupProgressMessage;

export interface AppLaunchedMessage {
  sessionId: string;
  deviceIdentifier: string;
}

interface DefaultAppInstalledMessage {
  sessionId: string;
  appId: string;
  appBuildId: string;
  deviceIdentifier: string;
}

/** HTTPS gateway endpoint URLs and auth credentials for connecting to a VM. */
export interface HttpsEndpoints {
  /** DCV streaming gateway endpoint URL. */
  dcv?: string | null;
  /** Innoactive Spatial endpoint URL. */
  spatialCommunication?: string | null;
  /** CloudXR signaling endpoint URL. */
  cloudXRSignaling?: string | null;
  /** Routing key (equals the VM ID) sent on requests through these endpoints. */
  routingKey?: string | null;
  /** Bearer token sent as the authorization header on requests through these endpoints. */
  authToken?: string | null;
}

interface CloudRenderedAppInstalledMessage extends DefaultAppInstalledMessage {
  ipAddress: string;
  /** @deprecated Prefer httpsEndpoints.dcv. */
  streamingGatewayEndpoint?: string | null;
  /** HTTPS gateway endpoints for connecting to the VM. */
  httpsEndpoints?: HttpsEndpoints | null;
}

export type AppInstalledMessage =
  | DefaultAppInstalledMessage
  | CloudRenderedAppInstalledMessage;

export interface RequestSessionMessage {
  appBuildId: string;
  deviceIdentifier: string;
  extraLaunchArguments?: string;
  organizationId: string;
  renderRegion?: string;
  vmSize?: string;
  vmImage?: string;
  forceColdVm: boolean;
  xrEncryption?: boolean;
  debugModeEnabled: boolean;
  virtualMachineId?: string;
  requireDCV: boolean;
  requireXRGateway: boolean;
  // When renderRegion is omitted, Session Management selects the region itself
  // and fails over across regions on capacity errors. These let it make that
  // choice well. SM rejects them if renderRegion is also set, so they must only
  // be sent for automatic (server-side) region selection.
  position?: { latitude: number; longitude: number };
  regionLatencyMs?: { [region: string]: number };
}

export interface ErrorMessage {
  sessionId?: string;
  message?: string;
  knownSessionError?: string;
  knownSessionErrorCode?: KnownErrorCodes;
}

export type SessionOverviewMessage =
  | Record<string, never>
  | DefaultSessionOverviewMessage
  | CloudRenderedSessionOverviewMessage;

export interface ReportedIssue {
  id: UUID;
  issueCode: number;
  sourceDeviceIdentifier: string;
  message: string;
  params?: { [key: string]: string };
}

export interface IssueHandledMessage {
  id: UUID;
  issueCode: number;
  message: string;
  params?: { [key: string]: string };
}

export enum KnownIssueCodes {
  BadLatency = 2001,
  HighJitter = 2002,
  HighPackageLoss = 2003,
  LowBandwidth = 2004,
  OpenUrl = 100200,
  OpenCallbackUrl = 100201,
}

export enum KnownErrorCodes {
  SessionTimeout = 5000,
  NoFixedIpAvailable = 5010,
  NoVmAvailable = 5020,
  CouldNotProvisionVm = 5030,
  ImageNotFound = 5040,
  NoUnmanagedNodeAvailable = 5050,
  AuthTokenNotMatching = 5060,
}
